import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Seo from '../../components/Seo';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import GoalTypeSelector from './components/GoalTypeSelector';
import GoalParametersForm from './components/GoalParametersForm';
import CoverImageUpload from './components/CoverImageUpload';
import VisibilitySettings from './components/VisibilitySettings';
import ChallengePreview from './components/ChallengePreview';
import { useAuth } from '../../auth/AuthProvider';
import { supabase } from '../../lib/supabaseClient';
import { makeSafeObjectPath, uploadToBucket } from '../../utils/storage';
import { compressImageUnderBytes } from '../../utils/imageCompression';
import AICoverImageWizard from '../project-creation/components/AICoverImageWizard';

const ChallengeCreation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const projectId = location?.state?.projectId || '';
  const [currentStep, setCurrentStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const [isAICoverOpen, setIsAICoverOpen] = useState(false);

  const parseTags = (raw) => {
    const items = String(raw || '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    return Array.from(new Set(items));
  };

  const [notice, setNotice] = useState({
    open: false,
    title: '',
    message: '',
    variant: 'info',
    primaryLabel: '확인',
    onPrimary: null,
  });

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    tagsText: '',
    coverImage: null,
    coverImageFile: null,
    goalType: '',
    parameters: {},
    settings: {
      defaultPublic: true,
      sequentialUnlock: true,
      celebrationAnimation: false,
    },
  });

  const [errors, setErrors] = useState({});

  const steps = [
    { id: 1, name: '기본 정보', icon: 'FileText' },
    { id: 2, name: '목표 설정', icon: 'Target' },
    { id: 3, name: '추가 설정', icon: 'Settings' },
    { id: 4, name: '미리보기', icon: 'Eye' },
  ];

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData?.title?.trim()) {
        newErrors.title = '챌린지 제목을 입력하세요';
      }
      if (!formData?.description?.trim()) {
        newErrors.description = '챌린지 설명을 입력하세요';
      }
    }

    if (step === 2) {
      if (!formData?.goalType) {
        newErrors.goalType = '목표 유형을 선택하세요';
      }

      if (formData?.goalType === 'fixed') {
        if (!formData?.parameters?.targetCount || formData?.parameters?.targetCount < 1) {
          newErrors.targetCount = '목표 횟수는 1 이상이어야 합니다';
        }
      }

      if (formData?.goalType === 'split') {
        if (!formData?.parameters?.totalGoal || formData?.parameters?.totalGoal < 1) {
          newErrors.totalGoal = '전체 목표 수치를 입력하세요';
        }
        if (!formData?.parameters?.splitCount || formData?.parameters?.splitCount < 2) {
          newErrors.splitCount = '분할 단계는 2 이상이어야 합니다';
        }
      }

      if (formData?.goalType === 'thirty-day') {
        if (!formData?.parameters?.startDate) {
          newErrors.startDate = '시작 날짜를 선택하세요';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const openNotice = ({ title, message, variant = 'info', primaryLabel = '확인', onPrimary = null }) => {
    setNotice({ open: true, title, message, variant, primaryLabel, onPrimary });
  };

  const closeNotice = () => {
    setNotice((prev) => ({ ...prev, open: false }));
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, steps?.length));
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSaveDraft = async () => {
    openNotice({
      title: '임시 저장',
      message: '임시 저장 기능은 추후 구현됩니다',
      variant: 'info',
    });
  };

  const handleCreateChallenge = async () => {
    if (!validateStep(1) || !validateStep(2)) {
      openNotice({
        title: '입력 확인',
        message: '필수 항목을 모두 입력해주세요',
        variant: 'warning',
      });
      return;
    }

    if (!user?.id) {
      openNotice({
        title: '로그인이 필요합니다',
        message: '계속하려면 로그인해주세요.',
        variant: 'warning',
        primaryLabel: '로그인',
        onPrimary: () => navigate('/login-registration'),
      });
      return;
    }

    if (!projectId) {
      openNotice({
        title: '목표를 찾을 수 없습니다',
        message: '목표 상세에서 다시 시도해주세요.',
        variant: 'error',
        primaryLabel: '목표 목록으로',
        onPrimary: () => navigate('/project-dashboard'),
      });
      return;
    }

    setIsSaving(true);

    try {
      const normalizedGoalType = formData?.goalType === 'thirty-day' ? 'thirty_day' : formData?.goalType;
      const totalGoals = (() => {
        if (normalizedGoalType === 'fixed') return Number(formData?.parameters?.targetCount || 0);
        if (normalizedGoalType === 'split') return Number(formData?.parameters?.totalGoal || 0);
        if (normalizedGoalType === 'thirty_day') return 30;
        return 0;
      })();

      const targetCount = normalizedGoalType === 'fixed'
        ? Number(formData?.parameters?.targetCount || 0)
        : null;

      const { data: created, error: createError } = await supabase
        .from('ch_challenges')
        .insert({
          project_id: projectId,
          owner_id: user.id,
          title: formData?.title?.trim(),
          description: formData?.description?.trim(),
          tags: parseTags(formData?.tagsText),
          goal_type: normalizedGoalType,
          target_count: targetCount,
          total_goals: totalGoals,
          default_public: !!formData?.settings?.defaultPublic,
          sequential_unlock: !!formData?.settings?.sequentialUnlock,
          meta: {
            parameters: formData?.parameters || {},
            settings: formData?.settings || {},
          },
        })
        .select('*')
        .single();

      if (createError) {
        openNotice({
          title: '챌린지 생성 실패',
          message: createError?.message || '챌린지 생성에 실패했습니다',
          variant: 'error',
        });
        return;
      }

      let coverImagePath = '';
      const file = formData?.coverImageFile;
      if (file) {
        if (!file?.type?.startsWith('image/')) {
          openNotice({
            title: '업로드 실패',
            message: '이미지 파일만 업로드할 수 있습니다',
            variant: 'error',
          });
          return;
        }

        let blob = file;
        if (file.size > 1024 * 1024) {
          blob = await compressImageUnderBytes(file, 1024 * 1024);
        }

        const path = makeSafeObjectPath({
          userId: user.id,
          originalName: file?.name,
          prefix: 'covers',
        });

        const { error: uploadError } = await uploadToBucket({
          bucket: 'challenge-covers',
          path,
          blob,
          contentType: blob?.type || 'image/jpeg',
          cacheControl: '31536000',
        });

        if (uploadError) {
          openNotice({
            title: '업로드 실패',
            message: uploadError?.message || '이미지 업로드에 실패했습니다',
            variant: 'error',
          });
          return;
        }

        coverImagePath = path;
      }

      if (coverImagePath) {
        const { error: updateError } = await supabase
          .from('ch_challenges')
          .update({
            cover_image_path: coverImagePath,
            cover_image_alt: formData?.title?.trim() || 'Challenge cover image',
          })
          .eq('id', created.id);

        if (updateError) {
          openNotice({
            title: '저장 실패',
            message: updateError?.message || '챌린지 이미지 저장에 실패했습니다',
            variant: 'error',
          });
          return;
        }
      }

      openNotice({
        title: '생성 완료',
        message: '챌린지가 생성되었습니다!',
        variant: 'success',
        primaryLabel: '목표로 돌아가기',
        onPrimary: () => navigate(`/project-detail?id=${projectId}`),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const contextActions = [
    {
      label: '임시 저장',
      icon: 'Save',
      variant: 'outline',
      onClick: handleSaveDraft,
      description: '작성 중인 내용을 저장합니다',
    },
    {
      label: '챌린지 생성',
      icon: 'Check',
      variant: 'default',
      onClick: handleCreateChallenge,
      description: '챌린지를 즉시 활성화합니다',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Seo 
        title="챌린지 생성 - 새로운 도전 시작 | 꿈현.Labs" 
        description="꿈현.Labs에서 새로운 챌린지를 생성하고 도전을 시작하세요. 목표 설정, 진행 추적, 성취 공유까지 체계적인 챌린지 관리로 꿈을 현실로 만들어보세요."
        canonicalPath="/challenge-creation" 
      />
      <PrimaryNavigation />
      <BreadcrumbNavigation />
      <main className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-6 md:py-8 lg:py-12 mt-16 mb-20 lg:mb-8">
        <div className="mb-6 md:mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-primary/20 rounded-md flex items-center justify-center">
              <Icon name="Plus" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground">
                새 챌린지 만들기
              </h1>
              <p className="text-sm md:text-base text-muted-foreground caption mt-1">
                목표를 설정하고 증명을 통해 달성하세요
              </p>
            </div>
          </div>
        </div>

        <div className="mb-6 md:mb-8">
          <div className="flex items-center justify-between mb-4">
            {steps?.map((step, index) => (
              <React.Fragment key={step?.id}>
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center transition-gaming ${
                      currentStep >= step?.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {currentStep > step?.id ? (
                      <Icon name="Check" size={20} />
                    ) : (
                      <Icon name={step?.icon} size={20} />
                    )}
                  </div>
                  <span
                    className={`text-xs md:text-sm font-medium mt-2 caption ${
                      currentStep >= step?.id ? 'text-foreground' : 'text-muted-foreground'
                    }`}
                  >
                    {step?.name}
                  </span>
                </div>
                {index < steps?.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-2 rounded-full transition-gaming ${
                      currentStep > step?.id ? 'bg-primary' : 'bg-muted'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2 space-y-6 md:space-y-8">
            {currentStep === 1 && (
              <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6 lg:p-8 space-y-6">
                <div>
                  <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-2">
                    기본 정보
                  </h2>
                  <p className="text-sm text-muted-foreground caption">
                    챌린지의 기본 정보를 입력하세요
                  </p>
                </div>

                <Input
                  type="text"
                  label="챌린지 제목"
                  description="챌린지를 대표하는 제목을 입력하세요"
                  placeholder="예: 30일 운동 챌린지"
                  value={formData?.title}
                  onChange={(e) => setFormData({ ...formData, title: e?.target?.value })}
                  error={errors?.title}
                  required
                  maxLength={50}
                />

                <div>
                  <label className="text-sm md:text-base font-heading font-semibold text-foreground block mb-2">
                    챌린지 설명 <span className="text-destructive">*</span>
                  </label>
                  <p className="text-xs md:text-sm text-muted-foreground caption mb-3">
                    챌린지의 목적과 방법을 자세히 설명하세요
                  </p>
                  <textarea
                    value={formData?.description}
                    onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                    placeholder="예: 매일 30분씩 운동하고 증명 사진을 업로드하는 챌린지입니다. 건강한 습관을 만들어보세요!"
                    rows={6}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-gaming resize-none"
                  />
                  {errors?.description && (
                    <div className="flex items-start space-x-2 mt-2">
                      <Icon name="AlertCircle" size={14} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-destructive">{errors?.description}</p>
                    </div>
                  )}
                </div>

                <Input
                  type="text"
                  label="태그"
                  description="쉼표(,)로 구분하여 입력하세요"
                  placeholder="예: 운동, 습관, 30일"
                  value={formData?.tagsText}
                  onChange={(e) => setFormData({ ...formData, tagsText: e?.target?.value })}
                  maxLength={200}
                />

                <CoverImageUpload
                  coverImage={formData?.coverImage}
                  headerActions={(
                    <Button
                      variant="outline"
                      iconName="Sparkles"
                      iconPosition="left"
                      onClick={() => setIsAICoverOpen(true)}
                      disabled={isSaving}
                    >
                      AI 커버 이미지 생성
                    </Button>
                  )}
                  onImageChange={(image, file) => {
                    if (!image || !file) {
                      setFormData({ ...formData, coverImage: null, coverImageFile: null });
                      return;
                    }

                    if (!file?.type?.startsWith('image/')) {
                      setErrors((prev) => ({ ...prev, coverImage: '이미지 파일만 업로드할 수 있습니다' }));
                      setFormData({ ...formData, coverImage: null, coverImageFile: null });
                      return;
                    }

                    setErrors((prev) => ({ ...prev, coverImage: '' }));
                    setFormData({ ...formData, coverImage: image, coverImageFile: file });
                  }}
                  error={errors?.coverImage}
                />
              </div>
            )}

            {currentStep === 2 && (
              <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6 lg:p-8 space-y-6">
                <div>
                  <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-2">
                    목표 설정
                  </h2>
                  <p className="text-sm text-muted-foreground caption">
                    챌린지의 목표 유형과 세부 사항을 설정하세요
                  </p>
                </div>

                <GoalTypeSelector
                  selectedType={formData?.goalType}
                  onTypeChange={(type) =>
                    setFormData({ ...formData, goalType: type, parameters: {} })
                  }
                />

                {errors?.goalType && (
                  <div className="flex items-start space-x-2 p-3 bg-destructive/10 rounded-md border border-destructive/20">
                    <Icon name="AlertCircle" size={16} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-destructive">{errors?.goalType}</p>
                  </div>
                )}

                {formData?.goalType && (
                  <GoalParametersForm
                    goalType={formData?.goalType}
                    parameters={formData?.parameters}
                    onParametersChange={(params) =>
                      setFormData({ ...formData, parameters: params })
                    }
                    errors={errors}
                  />
                )}
              </div>
            )}

            {currentStep === 3 && (
              <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6 lg:p-8 space-y-6">
                <div>
                  <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-2">
                    추가 설정
                  </h2>
                  <p className="text-sm text-muted-foreground caption">
                    증명 공개 범위와 챌린지 동작을 설정하세요
                  </p>
                </div>

                <VisibilitySettings
                  settings={formData?.settings}
                  onSettingsChange={(settings) =>
                    setFormData({ ...formData, settings })
                  }
                />
              </div>
            )}

            {currentStep === 4 && (
              <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6 lg:p-8">
                <ChallengePreview formData={formData} />
              </div>
            )}

            <div className="flex items-center justify-between gap-3">
              <Button
                variant="outline"
                iconName="ChevronLeft"
                iconPosition="left"
                onClick={handlePrevious}
                disabled={currentStep === 1}
              >
                이전
              </Button>

              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  iconName="Save"
                  iconPosition="left"
                  onClick={handleSaveDraft}
                  loading={isSaving}
                >
                  임시 저장
                </Button>

                {currentStep < steps?.length ? (
                  <Button
                    variant="default"
                    iconName="ChevronRight"
                    iconPosition="right"
                    onClick={handleNext}
                  >
                    다음
                  </Button>
                ) : (
                  <Button
                    variant="default"
                    iconName="Check"
                    iconPosition="left"
                    onClick={handleCreateChallenge}
                    loading={isSaving}
                  >
                    챌린지 생성
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="hidden lg:block">
            <div className="sticky top-24">
              <ChallengePreview formData={formData} />
            </div>
          </div>
        </div>
      </main>

      <AICoverImageWizard
        open={isAICoverOpen}
        onClose={() => setIsAICoverOpen(false)}
        kind="challenge"
        title={formData?.title}
        description={formData?.description}
        onApply={(image, file) => {
          setErrors((prev) => ({ ...prev, coverImage: '' }));
          setFormData((prev) => ({ ...prev, coverImage: image, coverImageFile: file }));
        }}
      />

      {notice?.open && (
        <div className="fixed inset-0 z-dialog bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-card rounded-lg shadow-gaming-lg p-6 md:p-8 max-w-md w-full border border-border">
            <div className="flex items-start space-x-4 mb-6">
              <div
                className={`flex-shrink-0 w-12 h-12 rounded-md flex items-center justify-center ${
                  notice?.variant === 'success'
                    ? 'bg-success/20'
                    : notice?.variant === 'error'
                      ? 'bg-destructive/20'
                      : notice?.variant === 'warning'
                        ? 'bg-warning/20'
                        : 'bg-primary/20'
                }`}
              >
                <Icon
                  name={notice?.variant === 'success' ? 'CheckCircle' : notice?.variant === 'error' ? 'XCircle' : notice?.variant === 'warning' ? 'AlertTriangle' : 'Info'}
                  size={24}
                  color={notice?.variant === 'success' ? 'var(--color-success)' : notice?.variant === 'error' ? 'var(--color-destructive)' : notice?.variant === 'warning' ? 'var(--color-warning)' : 'var(--color-primary)'}
                />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">{notice?.title || '안내'}</h3>
                <p className="text-sm text-muted-foreground">{notice?.message}</p>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3">
              <Button
                variant="outline"
                onClick={closeNotice}
                disabled={isSaving}
              >
                닫기
              </Button>
              <Button
                variant={notice?.variant === 'error' ? 'destructive' : 'default'}
                onClick={() => {
                  const fn = notice?.onPrimary;
                  closeNotice();
                  if (typeof fn === 'function') fn();
                }}
                disabled={isSaving}
              >
                {notice?.primaryLabel || '확인'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChallengeCreation;